"use client";

import clsx from "clsx";
import type { EventLogState, LogisticsMilestone } from "@/lib/sse/useSSE";
import { CraneIllustration } from "@/components/scenes/shared/illustrations/CraneIllustration";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Counterpart view for screen 2.5 — Al Madar's buyer-side shipment tracking.
 */
export function SmartLogisticsImporter({ ev, className }: { ev: EventLogState; className?: string }) {
  const t = useTranslations("smartLogisticsImporter");
  const milestones = ev.logisticsMilestones;
  const reached    = new Set(milestones.map((m) => m.milestone));
  const last       = milestones[milestones.length - 1];
  const tempC      = last?.tempC ?? -18;

  const customsCleared = ev.customsCleared.dubaiTrade && ev.customsCleared.moccae;
  const loaded = reached.has("Loading");
  const stage =
    reached.has("Arrival")   ? "arrived"   :
    reached.has("Departure") ? "in-transit":
    reached.has("Loading")   ? "loading"   :
                                 "awaiting";

  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">
              {t("inboundKicker")}
            </p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">
              {t("heading")}
            </h3>
            <p className="mt-1 text-[12px] text-orbitra-teal-soft">
              {t("detail")}
            </p>
          </div>
          <span className={clsx(
            "rounded-full border px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider",
            stage === "arrived"     ? "border-policy-pass/50 bg-policy-pass/10 text-policy-pass" :
            stage === "in-transit"  ? "border-state-traced/50 bg-state-traced/10 text-state-traced" :
            stage === "loading"     ? "border-state-traced/40 bg-state-traced/10 text-state-traced" :
                                       "border-orbitra-muted/30 text-orbitra-muted",
          )}>
            {stage === "arrived"    && t("badgeArrived")}
            {stage === "in-transit" && t("badgeAtSea")}
            {stage === "loading"    && t("badgeLoading")}
            {stage === "awaiting"   && t("badgeAwaiting")}
          </span>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-3 gap-4">
        <div className="glass col-span-2 flex flex-col p-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("milestoneTrackerKicker")}</p>

          <ol className="mt-3 flex flex-col gap-2">
            <Step n={1} label={t("step1Loading")}   reached={reached.has("Loading")}   />
            <Step n={2} label={t("step2Departure")} reached={reached.has("Departure")} />
            <Step n={3} label={t("step3Arrival")}   reached={reached.has("Arrival")}   />
            <Step n={4} label={t("step4DubaiTrade")} reached={ev.customsCleared.dubaiTrade} dependsOn={!reached.has("Arrival")} />
            <Step n={5} label={t("step5Moccae")}    reached={ev.customsCleared.moccae}     dependsOn={!reached.has("Arrival")} />
            <Step n={6} label={t("step6Settlement")} reached={customsCleared} dependsOn={!customsCleared} highlight />
          </ol>

          <div className={clsx(
            "mt-auto rounded-md border px-3 py-2 text-[11px]",
            customsCleared
              ? "border-state-financed/50 bg-state-financed/10 text-state-financed"
              : "border-state-traced/30 bg-state-traced/8 text-state-traced",
          )}>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em]">
              {customsCleared ? t("triggerArmed") : t("waitingForCustoms")}
            </p>
            <p className="mt-1 text-orbitra-paper">
              {customsCleared ? t("bodyCustomsCleared") : t("bodyCustomsPending")}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="glass p-4">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">{t("coldChainKicker")}</p>
            <div className="mt-3 flex items-end gap-2">
              <span className="font-display text-3xl font-semibold text-state-traced">{tempC}°C</span>
              <span className="pb-1 text-[10px] text-orbitra-teal-soft">{t("coldChainTarget")}</span>
            </div>
            <p className="mt-2 text-[10px] text-orbitra-muted">
              {t("coldChainSampling")}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-1 text-[9px]">
              {Array.from({ length: 12 }).map((_, i) => (
                <span
                  key={i}
                  className={clsx(
                    "h-1.5 rounded-full",
                    i < milestones.length * 4 ? "bg-state-traced" : "bg-orbitra-muted/20",
                  )}
                />
              ))}
            </div>
          </div>

          <div className="glass p-4 text-[11px]">
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("arrivalPrepKicker")}</p>
            <ul className="mt-2 space-y-1 text-orbitra-teal-soft">
              <li>{t("arrivalPrep1")}</li>
              <li>{t("arrivalPrep2")}</li>
              <li>{t("arrivalPrep3")}</li>
              <li>{t("arrivalPrep4")}</li>
            </ul>
          </div>

          <CraneIllustration loaded={loaded} />
        </div>
      </div>
    </div>
  );
}

function Step({
  n, label, reached, dependsOn, highlight,
}: { n: number; label: string; reached: boolean; dependsOn?: boolean; highlight?: boolean }) {
  const t = useTranslations("smartLogisticsImporter");
  return (
    <li className={clsx(
      "flex items-center gap-3 rounded-md border px-3 py-2 text-[11px] transition-colors duration-state ease-state",
      reached
        ? "border-policy-pass/40 bg-policy-pass/8 text-policy-pass"
        : dependsOn
          ? "border-orbitra-muted/30 bg-orbitra-ink/30 text-orbitra-muted"
          : "border-state-traced/30 bg-state-traced/8 text-state-traced",
      highlight && reached && "border-state-financed/50 bg-state-financed/10 text-state-financed shadow-[0_0_18px_var(--state-financed-glow)]",
    )}>
      <span className="font-mono text-[10px] font-semibold">{n}</span>
      <span className="flex-1 text-orbitra-paper">{label}</span>
      <span className="text-[10px] uppercase tracking-wider">
        {reached ? t("stepDone") : dependsOn ? t("stepAwaiting") : t("stepArmed")}
      </span>
    </li>
  );
}
